import { defineStore, acceptHMRUpdate } from 'pinia'
import { useMiscStore } from './misc'
import { useStatuteStore } from './statute'
import { groupByKey } from '@/lib/utils'
import { API } from '@/lib/api'

export const useMatterStore = defineStore('matter', {
	state: () => ({
		matters: [],
		matter: null,
		counts: [],
		adjustments: [],
		uid: null,
		focusCount: {},
		_loading: 0,
		error: null,
	}),

	actions: {
		async _setLoading(status, global = true) {
			if (status) {
				this.error = null
				this._loading = Math.max(this._loading + 1, 0)
			} else {
				this._loading = Math.max(this._loading - 1, 0)
			}

			if (global) {
				const miscStore = useMiscStore()
				miscStore.setLoading(status)
			}
		},

		async _setError(error = null, global = true) {
			this.error = error
			console.group(`MatterStore.Error`)
			console.warn(error)
			console.groupEnd()

			if (global) {
				const miscStore = useMiscStore()
				miscStore.setError(error)
			}
		},

		async setUid(user) {
			console.log('matter.setUid', user)
			this.uid = user.uid
		},

		async getMatters() {
			this._setLoading(true)

			let docsPath = [`matters`, this.uid, `matters`].join(`/`)
			if (!this.uid)
				docsPath = `matters`

			try {
				this.matters = await API.get(`/matters`)
			} catch (error) {
				this._setError(error)
			} finally {
				this._setLoading(false)
			}
		},

		async getMatter(matterId) {
			this._setLoading(true)

			return new Promise(async (resolve, reject) => {
				try {
					const {counts = [], adjustments = [], ...matter} = await API.get(`/matters/${matterId}`)
					this.matter = matter
					this.counts = counts
					this.adjustments = adjustments
					resolve(matter)
				} catch (error) {
					this._setError(error)
					reject(error)
				} finally {
					this._setLoading(false)
				}

				return this.matter
			})
		},

		async getFullMatter(matterId) {
			this._setLoading(true)

			let matter
			try {
				matter = await this.getMatter(matterId)
				this.matter = {
					...matter,
				}
			} catch (error) {
				this._setError(error)
			} finally {
				this._setLoading(false)
			}

			return this.matter
		},

		async addMatter(data, uid) {
			this._setLoading(true)

			try {
				const res = await API.post(`/matters`, data)
				this.matters.push(res)
				return res
			} catch (error) {
				this._setError(error)
			} finally {
				this._setLoading(false)
			}
		},

		async duplicateMatter(matterId, newData) {
			this._setLoading(true)

			return new Promise(async (resolve, reject) => {
				try {
					const { counts = [], adjustments = [], ...matter } = await API.post(`/matters/${matterId}/duplicate`, newData)
					this.matter = matter
					this.counts = counts
					this.adjustments = adjustments
					resolve(matter)
				} catch (error) {
					reject(error)
				} finally {
					this._setLoading(false)
				}
			})
		},

		async updateMatter(matterId, data, uid) {
			if (!matterId) return miscStore.setError(`Missing matter ID`, 'updateMatter:')

			this._setLoading(true)

			const { notes, ['@odata.etag']: etag, ...updateData} = data
			// const { notes, guidelineDetails, guidelineSummary, countSummary, ...updateData} = data
			try {
				const headers = {
					'If-Match': etag
				}
				const { counts = [], adjustments = [], ...matter } = await API.patch(`/matters/${matterId}`, updateData, {}, true, headers)
				this.matter = matter
				this.counts = counts
				this.adjustments = adjustments
				return this.matter
			} catch (error) {
				this._setError(false)
				throw error
			} finally {
				this._setLoading(false)
			}

			return this.matter
		},

		async removeMatter(matterId, data) {
			if (!matterId) return miscStore.setError(`Missing matter ID`, 'removeMatter:')
			this._setLoading(true)

			return new Promise(async (resolve, reject) => {
				try {
					const headers = {
						'If-Match': data[`@odata.etag`]
					}
					const { counts = [], adjustments = [], ...matter } = await API.delete(`/matters/${matterId}`, {}, true, headers)
					this.matter = matter
					this.counts = counts
					this.adjustments = adjustments

					const idx = this.matters.findIndex(m => m.matterId == matterId)
					this.matters.splice(idx, 1)
					resolve(matter)
				} catch (error) {
					reject(error)
					this._setError(error)
				} finally {
					this._setLoading(false)
				}
			})
		},

		async addCount(matterId, data, withStatute = true) {
			if (!matterId) return miscStore.setError(`Missing matter ID`, 'addCount:')
			this._setLoading(true)

			return new Promise(async (resolve, reject) => {
				try {
					const {counts = [], adjustments = [], ...matter} = await API.post(`/matters/${matterId}/counts`, data)
					this.matter = matter
					this.counts = counts
					this.adjustments = adjustments

					resolve(counts)
				} catch (error) {
					reject(error)
					this._setError(error)
				} finally {
					this._setLoading(false)
				}
			})
		},


		async removeCount(matterId, count, noUpdate = false) {
			if (!matterId || !count.countId) return miscStore.setError(`Missing matter ID`, 'removeCount:')
			this._setLoading(true)

			return new Promise(async (resolve, reject) => {
				try {
					const headers = {
						'If-Match': count[`@odata.etag`]
					}
					const {counts = [], adjustments = [], ...matter} = await API.delete(`/matters/${matterId}/counts/${count.countId}`, {}, true, headers)
					this.matter = matter
					this.counts = counts
					this.adjustments = adjustments
					resolve(counts)
				} catch (error) {
					reject(error)
					this._setError(error)
				} finally {
					this._setLoading(false)
				}
			})
		},

		async getCount(matterId, countId, withStatute = true) {
			if (!matterId) return miscStore.setError(`Missing matter ID`, 'getCount:')
			this._setLoading(true)

			let count
			try {
				count = await API.get(`/matters/${matterId}/counts/${countId}`)
			} catch (error) {
				this._setError(error)
			} finally {
				this._setLoading(false)
			}

			count.pecuniary = count.pecuniary || {}
			count.minFine = count.minFine || {}
			count.maxFine = count.maxFine || {}

			if (withStatute) {
				const statuteStore = useStatuteStore()
				count.statuteData = await statuteStore.getStatuteSection(count.statute, count.section.value)
			}

			this.count = count

			return count
		},

		async updateCount(matterId, countId, data, uid) {
			if (!matterId) return miscStore.setError(`Missing matter ID`, 'updateCount:')
			if (!countId) return miscStore.setError(`Missing count ID`)

			this._setLoading(true)

			return new Promise(async (resolve, reject) => {
				uid = uid || this.uid
				let docPath = [`matters`, uid, `matters`,matterId].join(`/`)
				if (!uid)
					docPath = [`matters`,matterId].join(`/`)

				try {
					const { ['@odata.etag']: etag, ...rest } = data
					const headers = {
						'If-Match': etag
					}

					const { countId: _, guideline, section, statute, notes, ...toUpdate  } = rest
					const {counts = [], adjustments = [], ...matter} = await API.patch(`/matters/${matterId}/counts/${countId}`, toUpdate, {}, true, headers)
					this.matter = matter
					this.counts = counts
					this.adjustments = adjustments

					this.matter = {...this.matter}
					resolve(matter)
					return data
				} catch (error) {
					if (error.data) {
						const { counts = [], adjustments = [], ...matter} = error.data
						this.matter = matter
						this.counts = counts
						this.adjustments = adjustments
					}
					this._setError(error)
					reject(error)
				} finally {
					this._setLoading(false)
				}

			})
		},

		async addAdjustment(matterId, countId, group, data) {
			if (!matterId) return miscStore.setError(`Missing matter ID`, 'addAdjustment:')
			// if (!countId) return miscStore.setError(`Missing count ID`)
			this._setLoading(true)

			const toAdd = {
				...data,
				countId,
				group,
			}

			return new Promise(async (resolve, reject) => {
				try {
					const {counts = [], adjustments = [], ...matter} = await API.post(`/matters/${matterId}/adjustments`, toAdd)
					this.matter = matter
					this.counts = counts
					this.adjustments = adjustments
					resolve(adjustments)
				} catch (error) {
					if (error.data) {
						const { counts = [], adjustments = [], ...matter} = error.data
						this.matter = matter
						this.counts = counts
						this.adjustments = adjustments
					}
					reject(error)
				} finally {
					this._setLoading(false)
				}
			})
		},

		async updateAdjustment(matterId, countId, group, data) {
			if (!matterId) return miscStore.setError(`Missing matter ID`, 'updateAdjustment:')
			this._setLoading(true)

			return new Promise(async (resolve, reject) => {
				try {
					const headers = {
						'If-Match': data[`@odata.etag`]
					}

					const { guideline, section, statute, ...toUpdate  } = data
					const {counts = [], adjustments = [], ...matter} = await API.patch(`/matters/${matterId}/adjustments/${data.adjustmentId}`, toUpdate, {}, true, headers)
					this.matter = matter
					this.counts = counts
					this.adjustments = adjustments

					resolve(adjustments)
				} catch (error) {
					this._setError(error)
					reject(error)
				} finally {
					this._setLoading(false)
				}

			})
		},

		async getAdjustments(matterId) {
			const miscStore = useMiscStore()
			if (!matterId) return miscStore.setError(`Missing matter ID`, 'getAdjustments:')
			miscStore.setLoading(true)
			miscStore.setError(null)

			let docPath = [`matters`, this.uid, `matters`,matterId].join(`/`)
			if (!this.uid)
				docPath = [`matters`,matterId].join(`/`)

			try {
				this.adjustments = await API.get(`/matters/${matterId}/adjustments`)
			} catch (error) {
				miscStore.setError(error)
			} finally {
				miscStore.setLoading(false)
			}

			return this.adjustments
		},

		async setCountGroup(matterId, count, group) {
			if (!matterId) return miscStore.setError(`Missing matter ID`)
			if (!count.countId) return miscStore.setError(`Missing count ID`)
			this._setLoading(true)

			return new Promise(async(resolve, reject) => {
				try {
					const headers = {
						'If-Match': count[`@odata.etag`]
					}
					const {counts = [], adjustments = [], ...matter} = await API.patch(`/matters/${matterId}/counts/${count.countId}/group`, { group }, {}, true, headers)
					this.matter = matter
					this.counts = counts
					this.adjustments = adjustments
					resolve(counts)
				} catch (error) {
					this._setError(error)
					reject(error)
				} finally {
					this._setLoading(false)
				}
			})
		},

		async setCountNumber(matterId, count, change) {
			if (!matterId) return miscStore.setError(`Missing matter ID`)
			if (!count?.countId) return miscStore.setError(`Missing count ID`)
			this._setLoading(true)

			return new Promise(async (resolve, reject) => {
				try {
					const headers = {
						'If-Match': count[`@odata.etag`]
					}
					const {counts = [], adjustments = [], ...matter} = await API.patch(`/matters/${matterId}/counts/${count.countId}/number`, { change }, {}, true, headers)
					this.matter = matter
					this.counts = counts
					this.adjustments = adjustments

				} catch (error) {
					this._setError(error)
					reject(error)
				} finally {
					this._setLoading(false)
				}
			})

		},

		async getUsers() {
			this._setLoading(true)

			return new Promise(async (resolve, reject) => {
				try {
					const users = await API.get(`/matters/users`)
					resolve(users)
				} catch (error) {
					reject(error)
				} finally {
					this._setLoading(false)
				}
			})
		},

		setFocus(countId, groupId) {
			const count = this.counts.find(c => c.countId == countId)
			if (count) {
				this.focusCount = {countId: count.countId, group: count.group, }
			} else if (groupId) {
				this.focusCount = {countId: null, group: groupId}
			}

		},

		unsetMatters() {
			this.matters = []
		},

		unsetMatter() {
			this.matter = {}
		},

		unsetCounts() {
			this.counts = []
		},

		unsetAdjustments() {
			this.adjustments = []
		},

	},

	getters: {
		countsByGroup: state => (groupId = 0) => {
			return state.counts?.filter(c => !c.parent).filter(c => Number(c.group) == Number(groupId)).sort((a, b) => a.number > b.number ? 1 : -1)
		},
		adjustmentByGroup: state => (groupId = 0) => {
			const [adjustment] = state.adjustments?.filter(c => Number(c.group) == Number(groupId)).sort((a, b) => a.createdAt > b.createdAt ? 1 : -1)
			return adjustment
		},
		adjustmentByCount: state => (countId) => {
			const [adjustment] = state.adjustments?.filter(c => c.countId == countId).sort((a, b) => a.createdAt > b.createdAt ? 1 : -1)
			return adjustment
		},
		adjustmentOffensesByCount: state => (countId) => {
			const [adjustment] = state.adjustments?.filter(c => c.countId == countId).sort((a, b) => a.createdAt > b.createdAt ? 1 : -1)

			const s = adjustment?.offenses?.map(o => o.sectionId).filter(n => n)
			for (const k in s) {
				const v = s[k]
				if (v === true) {
					toPush.push(k)
				} else if (v != ''){
					toPush.push(v)
				}
			}
		},
		theCount: state => (countId) => {
			return state.counts.find(c => c.$id == countId)
		},
		groups: state => {
			let counts = (state.counts || []).filter(c => !c.parent)
			if (!counts.length) return []
			const g = groupByKey(counts, 'group')
			const keys = [...new Set(Object.keys(g).map(k => Number(k)))]

			let groups = []
			// if (!keys.includes(0)) {
				// keys.unshift(0)
			// }

			groups = [
				...groups,
				...keys.map(k => ({
					id: Number(k),
					title: k == 0 ? `Ungrouped` : `Group ${k}`,
					counts: g[k]?.length || 0,
				}))
			]

			return groups
		},
		childCounts: state => (countId) => {
			return state.counts.filter(c => c.parent == countId)
		},
		matterNotes: state => {
			return state.matter?.notes || []
		},
		isLoading: state => state._loading > 0,
	},
})

if (import.meta.hot) {
	import.meta.hot.accept(acceptHMRUpdate(useMatterStore, import.meta.hot))
}
